import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { AppProvider } from './context/AppContext';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import LocationSelect from './pages/LocationSelect';
import { getDefaultTasks } from './context/AppContext';
import Dashboard from './pages/Dashboard';
import BuyerBudget from './pages/BuyerBudget';
import MyScenarios from './pages/MyScenarios';
import Inspection from './pages/Inspection';
import Insurance from './pages/Insurance';
import MoveIn from './pages/MoveIn';
import Documents from './pages/Documents';
import Reports from './pages/Reports';
import Collaboration from './pages/Collaboration';
import AIAssistant from './pages/AIAssistant';
import ContractReview from './pages/ContractReview';
import SettingsPage from './pages/Settings';
import { AlertTriangle, Home, Key } from 'lucide-react';

function NotFound() {
  const navigate = useNavigate();
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px' }}>
      <AlertTriangle size={64} color="var(--primary)" style={{ marginBottom: 20 }} />
      <h1 style={{ fontSize: 48, fontWeight: 800, marginBottom: 8, color: 'var(--dark)' }}>404</h1>
      <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12, color: 'var(--gray-600)' }}>Page Not Found</h2>
      <p style={{ color: 'var(--gray-500)', fontSize: 15, marginBottom: 32, maxWidth: 400, margin: '0 auto 32px' }}>
        The page you're looking for doesn't exist or has been moved.
      </p>
      <button onClick={() => navigate('/')} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '12px 28px', background: 'var(--primary)', color: 'white',
        borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 15, cursor: 'pointer',
      }}>
        <Home size={18} /> Back to Dashboard
      </button>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--gray-50)',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', background: 'var(--primary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <Key size={28} color="white" />
        </div>
        <p style={{ color: 'var(--gray-500)', fontSize: 15 }}>Loading...</p>
      </div>
    </div>
  );
}

function AppContent() {
  const { user, isLoading, isAuthenticated, loginWithGoogle, loginWithEmail, signUpWithEmail } = useAuth();
  const [locationChosen, setLocationChosen] = useState(() => {
    try {
      const saved = localStorage.getItem('closingday_state');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.settings && parsed.settings.isOttawa !== null && parsed.settings.isOttawa !== undefined) {
          return true;
        }
      }
    } catch (e) {}
    return false;
  });

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <LandingPage onLoginGoogle={loginWithGoogle} onLoginEmail={loginWithEmail} onSignUpEmail={signUpWithEmail} />;
  }

  if (!locationChosen) {
    return (
      <LocationSelect onSelect={(location) => {
        const isOttawa = location === 'ottawa';
        try {
          const saved = localStorage.getItem('closingday_state');
          const state = saved ? JSON.parse(saved) : {};
          state.settings = {
            ...(state.settings || {}),
            isOttawa,
            city: isOttawa ? 'Ottawa' : '',
          };
          if (!isOttawa) {
            state.budget = {
              ...(state.budget || {}),
              purchasePrice: 500000,
              annualIncome: 100000,
            };
          }
          if (!state.tasks || state.tasks.length === 0) {
            state.tasks = getDefaultTasks(isOttawa);
          }
          localStorage.setItem('closingday_state', JSON.stringify(state));
        } catch (e) {}
        setLocationChosen(true);
      }} />
    );
  }

  return (
    <AppProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/budget" element={<BuyerBudget />} />
          <Route path="/scenarios" element={<MyScenarios />} />
          <Route path="/inspection" element={<Inspection />} />
          <Route path="/insurance" element={<Insurance />} />
          <Route path="/move-in" element={<MoveIn />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/collaboration" element={<Collaboration />} />
          <Route path="/assistant" element={<AIAssistant />} />
          <Route path="/ai-assistant" element={<Navigate to="/assistant" replace />} />
          <Route path="/contract-review" element={<ContractReview />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </AppProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}
